// Payments tab — record / edit payments, receipts.
import { useState, useEffect } from "react";
import { doc, setDoc, deleteDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { packageDisplayState, paymentIsCurrent, clientToday } from "../domain";
import { fmt, uid, cl } from "../lib/format";
import { getActivePkg, pkgDisplayName, migrateLegacyClient } from "../lib/packages";
import { themeTokens } from "../lib/theme";
import { Modal, Btn, Inp, Sel, SD, Empty, Icon, ConfirmButton, useToast, useFormGuard } from "../ui/primitives";
import { DocumentOptions } from "../modals/documents";

export const Payments=({clients,setClients,payments=[],setPayments,theme,currency="₹",userId,profile,registerBack})=>{
  const toast=useToast();
  const [sel,setSel]=useState("");
  const [editPay,setEditPay]=useState(null);
  const [docOptions,setDocOptions]=useState(null);
  useFormGuard(!!(sel||editPay)); // recording/editing a payment — don't let sync clobber it
  useEffect(()=>{
    if(!registerBack)return;
    if(sel)registerBack("payments-sel",()=>{setSel("");});
    else registerBack("payments-sel",null);
  },[sel,registerBack]);
  useEffect(()=>{
    if(!registerBack)return;
    if(editPay)registerBack("payments-edit",()=>{setEditPay(null);setEditForm(null);});
    else registerBack("payments-edit",null);
  },[editPay,registerBack]); // payment being edited
  const [editForm,setEditForm]=useState(null);
  // `payments` + `setPayments` come from App state (App syncs the collection and
  // persists the LS cache) so a payment recorded here is on the Dashboard ledger
  // and in Reports immediately, offline included.
  const [form,setForm]=useState({amount:"",date:clientToday(),mode:"Cash",note:"",packageId:""});
  const {isDark,cb,bc,tc,mc}=themeTokens(theme);

  const client=clients.find(c=>c.id===sel);
  // Auto-select current package when client changes
  useEffect(()=>{
    if(client){
      const pkgIdToSelect=client.currentPackageId||(client.package?.type&&client.package.type!=="none"?"legacy":"");
      setForm(f=>({...f,packageId:pkgIdToSelect}));
    }
  },[sel]);

  // determine if a payment is "previous contract" — before package start date
  // Offline-first ordering: every setPayments/setClients optimistic update runs
  // FIRST; the setDoc/deleteDoc is fired after with .catch, never awaited before a
  // local update (the promise doesn't settle offline). client.paid moves only for
  // payments paymentIsCurrent() counts — symmetrically on add / edit / delete.
  const add=()=>{
    if(!sel){toast("Select a client first.","warning");return;}
    if(!form.amount||isNaN(parseFloat(form.amount))||parseFloat(form.amount)<=0){toast("Enter a valid amount.","warning");return;}
    const amount=parseFloat(form.amount);
    let resolvedClient=client;
    let justMigrated=false;
    if(!client.packageHistory?.length&&client.package?.type&&client.package.type!=="none"){
      resolvedClient=migrateLegacyClient(client);
      justMigrated=true;
    }
    const finalPkgId=(justMigrated&&(form.packageId==="legacy"||!form.packageId))?resolvedClient.currentPackageId:(form.packageId||(resolvedClient.currentPackageId||"legacy"));
    const p={id:uid(),clientId:sel,clientName:resolvedClient.name,...form,packageId:finalPkgId,amount};
    setPayments(prev=>[p,...prev]);
    setClients(prev=>prev.map(c=>{
      if(c.id!==sel)return c;
      const base=justMigrated?{...c,currentPackageId:resolvedClient.currentPackageId,packageHistory:resolvedClient.packageHistory}:c;
      const delta=paymentIsCurrent(p,base)?amount:0;
      return {...base,paid:(Number(base.paid)||0)+delta};
    }));
    setForm({amount:"",date:clientToday(),mode:"Cash",note:"",packageId:resolvedClient.currentPackageId||""});
    toast("Payment recorded.","success");
    if(userId){
      const{id,...data}=p;
      setDoc(doc(db,"users",userId,"payments",id),data).catch(e=>{console.error("Payment save failed:",e);toast("Payment didn't sync — it'll retry when you're online.","warning");});
    }
  };

  const saveEdit=()=>{
    if(!editForm?.amount)return;
    const oldPay=editPay;
    const updated={...oldPay,...editForm,amount:parseFloat(editForm.amount)};
    setPayments(prev=>prev.map(p=>p.id===oldPay.id?updated:p));
    const c=clients.find(x=>x.id===oldPay.clientId);
    if(c){
      const belonged=paymentIsCurrent(oldPay,c);
      const belongsNow=paymentIsCurrent(updated,c);
      let delta=0;
      if(belonged)delta-=Number(oldPay.amount)||0;
      if(belongsNow)delta+=Number(updated.amount)||0;
      if(delta!==0)setClients(prev=>prev.map(x=>x.id===oldPay.clientId?{...x,paid:Math.max(0,(Number(x.paid)||0)+delta)}:x));
    }
    setEditPay(null);setEditForm(null);
    if(userId){
      const{id,...data}=updated;
      setDoc(doc(db,"users",userId,"payments",id),data).catch(e=>{console.error("Payment edit save failed:",e);toast("Edit didn't sync — it'll retry when you're online.","warning");});
    }
  };

  const deletePay=(p)=>{
    setPayments(prev=>prev.filter(x=>x.id!==p.id));
    const c=clients.find(x=>x.id===p.clientId);
    if(c&&paymentIsCurrent(p,c)){
      setClients(prev=>prev.map(x=>x.id===p.clientId?{...x,paid:Math.max(0,(Number(x.paid)||0)-(Number(p.amount)||0))}:x));
    }
    toast("Payment deleted","info");
    if(userId)deleteDoc(doc(db,"users",userId,"payments",p.id)).catch(e=>console.error("Payment delete failed:",e));
  };
  const hist=payments.filter(p=>p.clientId===sel);
  // client.paid is the source of truth for the current package's balance
  const clientPs=client?packageDisplayState(client):null;
  const currentPaid=clientPs?clientPs.paid:0;
  const hasPackage=client?!!(getActivePkg(client)):false;

  return(
    <div className="space-y-5">
      {clients.length===0?<Empty icon="payments" message={`Add ${cl(profile,2).toLowerCase()} first.`} theme={theme}/>:(
        <>
          <SD label={`Select ${cl(profile)}`} value={sel?clients.find(c=>c.id===sel)?.name||"":""} onChange={v=>{const c=clients.find(x=>x.name===v);setSel(c?.id||"");}} options={[...clients].sort((a,b)=>a.name.localeCompare(b.name)).map(c=>c.name)} placeholder={`Search ${cl(profile,2).toLowerCase()}...`} theme={theme}/>
          {client&&(
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-xl p-3 text-center" style={{background:isDark?"#0f172a":"#f9fafb"}}>
                  <p className="text-lg font-black" style={{fontVariantNumeric:"tabular-nums",color:tc}}>{hasPackage?`${currency}${fmt(clientPs?.price||0)}`:"—"}</p>
                  <p className="text-xs" style={{color:mc}}>Total Fee</p>
                </div>
                <div className="rounded-xl p-3 text-center" style={{background:"#dcfce7"}}>
                  <p className="text-lg font-black text-green-700" style={{fontVariantNumeric:"tabular-nums"}}>{currency}{fmt(currentPaid)}</p>
                  <p className="text-xs text-green-500">{hasPackage?"Paid (Current)":"Total Paid"}</p>
                </div>
                {hasPackage&&(
                  <div className="rounded-xl p-3 text-center" style={{background:clientPs?.amountDue>0?"#fef2f2":"#dbeafe"}}>
                    <p className="text-lg font-black" style={{fontVariantNumeric:"tabular-nums",color:clientPs?.amountDue>0?"#dc2626":"#1d4ed8"}}>{currency}{fmt(clientPs?.amountDue>0?clientPs.amountDue:clientPs?.credit||0)}</p>
                    <p className="text-xs" style={{color:clientPs?.amountDue>0?"#dc2626":"#1d4ed8"}}>{clientPs?.amountDue>0?"Pending":"Advance"}</p>
                  </div>
                )}
                {!hasPackage&&(
                  <div className="rounded-xl p-3 text-center" style={{background:isDark?"#0f172a":"#f9fafb"}}>
                    <p className="text-lg font-black" style={{color:mc}}>—</p>
                    <p className="text-xs" style={{color:mc}}>No Package</p>
                  </div>
                )}
              </div>
              <div className="rounded-2xl p-5 shadow-sm border space-y-3" style={{background:cb,borderColor:bc}}>
                <p className="font-semibold text-sm" style={{color:tc}}>Record Payment</p>
                <div className="grid grid-cols-2 gap-3">
                  <Inp label={`Amount (${currency})`} type="text" inputMode="numeric" value={form.amount} onChange={e=>setForm(p=>({...p,amount:e.target.value.replace(/[^0-9.]/g,"")}))} placeholder="Enter amount" theme={theme}/>
                  <Inp label="Date" type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))} theme={theme}/>
                  <Sel label="Mode" value={form.mode} onChange={e=>setForm(p=>({...p,mode:e.target.value}))} theme={theme}>
                    {["Cash","UPI","Bank Transfer","Cheque"].map(m=><option key={m}>{m}</option>)}
                  </Sel>
                  <Inp label="Note" value={form.note} onChange={e=>setForm(p=>({...p,note:e.target.value}))} theme={theme}/>
                  <div className="col-span-2">
                    <Sel label="Payment For" value={form.packageId} onChange={e=>setForm(p=>({...p,packageId:e.target.value}))} theme={theme}>
                      <option value="">— Select Package —</option>
                      {(client.packageHistory||[]).slice().reverse().map(pkg=>(
                        <option key={pkg.packageId} value={pkg.packageId}>
                          {pkg.packageId===client.currentPackageId?"✓ ":""}{pkgDisplayName(pkg)} · {pkg.startDate||"No date"} {pkg.status==="expired"?"(Past)":pkg.status==="active"?"(Current)":""}
                        </option>
                      ))}
                      {(!client.packageHistory?.length&&client.package?.type&&client.package.type!=="none")&&(
                        <option value="legacy">✓ {pkgDisplayName(client.package)} · {client.package.startDate||"No date"} (Current)</option>
                      )}
                      <option value="unlinked">Unlinked / Other</option>
                    </Sel>
                  </div>
                </div>
                <Btn onClick={add} theme={theme} className="w-full justify-center"><Icon name="check" size={16}/>Record Payment</Btn>
              </div>
              {hist.length>0&&(
                <div className="rounded-2xl p-5 shadow-sm border" style={{background:cb,borderColor:bc}}>
                  <p className="font-semibold text-sm mb-3" style={{color:tc}}>Payment History</p>
                  {hist.sort((a,b)=>b.date?.localeCompare(a.date)).map(p=>(
                    <div key={p.id} className="flex items-start justify-between text-sm py-2.5" style={{borderTop:"1px dotted "+bc}}>
                      <div className="flex-1">
                        <p className="font-medium" style={{fontVariantNumeric:"tabular-nums",color:tc}}>{currency}{fmt(p.amount)} · {p.mode}</p>
                        <p className="text-xs" style={{color:mc}}>{p.date}{p.note&&` · ${p.note}`}</p>
                        {(()=>{
                          const pkg=(client.packageHistory||[]).find(x=>x.packageId===p.packageId);
                          const label=p.packageId==="unlinked"?"Unlinked":p.packageId==="legacy"?"Current Package":pkg?(pkg.type==="custom"?(pkg.customName||"Custom Pack"):`${(pkg.type||"Package").charAt(0).toUpperCase()+pkg.type.slice(1).replace("_"," ")} · ${pkg.startDate||""}`):p.prevContract?"Previous Package":null;
                          return label?<span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-semibold" style={{background:p.packageId===client.currentPackageId||p.packageId==="legacy"?"#dcfce7":"#fef3c7",color:p.packageId===client.currentPackageId||p.packageId==="legacy"?"#166534":"#92400e"}}>{label}</span>:null;
                        })()}
                      </div>
                      <div className="flex items-center gap-1.5 ml-2 flex-shrink-0">
                        <button onClick={()=>{setEditPay(p);setEditForm({amount:String(p.amount),date:p.date,mode:p.mode||"Cash",note:p.note||"",packageId:p.packageId||""});}}
                          className="p-1.5 rounded-lg font-semibold border transition-all"
                          style={{borderColor:isDark?"#334155":"#e5e7eb",color:mc}}><Icon name="edit" size={13}/></button>
                        <button onClick={()=>setDocOptions(p)}
                          className="p-1.5 rounded-lg font-semibold border transition-all"
                          style={{borderColor:theme?.primary,color:theme?.primary}}
                          title="Receipt / Invoice"><Icon name="receipt" size={13}/></button>

                      </div>
                    </div>
                  ))}
                </div>
              )}
              {docOptions&&<DocumentOptions payment={docOptions} client={client} profile={profile} currency={currency} theme={theme} payments={payments} onClose={()=>setDocOptions(null)}/>}
              {/* Edit Payment Modal */}
              {editPay&&editForm&&(
                <Modal title="Edit Payment" onClose={()=>{setEditPay(null);setEditForm(null);}} theme={theme}>
                  <div className="grid grid-cols-2 gap-3">
                    <Inp label={`Amount (${currency})`} type="text" inputMode="numeric" value={editForm.amount} onChange={e=>setEditForm(p=>({...p,amount:e.target.value.replace(/[^0-9.]/g,"")}))} theme={theme}/>
                    <Inp label="Date" type="date" value={editForm.date} onChange={e=>setEditForm(p=>({...p,date:e.target.value}))} theme={theme}/>
                    <Sel label="Mode" value={editForm.mode} onChange={e=>setEditForm(p=>({...p,mode:e.target.value}))} theme={theme}>
                      {["Cash","UPI","Bank Transfer","Cheque"].map(m=><option key={m}>{m}</option>)}
                    </Sel>
                    <Inp label="Note" value={editForm.note} onChange={e=>setEditForm(p=>({...p,note:e.target.value}))} theme={theme}/>
                    <div className="col-span-2">
                      <Sel label="Payment For" value={editForm.packageId||(client?.currentPackageId||"legacy")} onChange={e=>setEditForm(p=>({...p,packageId:e.target.value}))} theme={theme}>
                        <option value="">— Select Package —</option>
                        {(client.packageHistory||[]).slice().reverse().map(pkg=>(
                          <option key={pkg.packageId} value={pkg.packageId}>
                            {pkg.packageId===client.currentPackageId?"✓ ":""}{pkgDisplayName(pkg)} · {pkg.startDate||"No date"} {pkg.status==="expired"?"(Past)":pkg.status==="active"?"(Current)":""}
                          </option>
                        ))}
                        {(!client.packageHistory?.length&&client.package?.type&&client.package.type!=="none")&&(
                          <option value="legacy">✓ {pkgDisplayName(client.package)} · {client.package.startDate||"No date"} (Current)</option>
                        )}
                        <option value="unlinked">Unlinked / Other</option>
                      </Sel>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <ConfirmButton label={<><Icon name="trash" size={14}/>Delete</>} confirmLabel="Delete payment?" onConfirm={()=>{deletePay(editPay);setEditPay(null);setEditForm(null);}} danger className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-semibold text-white" style={{background:"#ef4444"}}/>
                    <div className="flex gap-3">
                      <Btn variant="secondary" onClick={()=>{setEditPay(null);setEditForm(null);}} theme={theme}>Cancel</Btn>
                      <Btn onClick={saveEdit} theme={theme}><Icon name="check" size={16}/>Save</Btn>
                    </div>
                  </div>
                </Modal>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};
